<template>
  <div class="flex flex-col h-screen overflow-y-auto bg-gray-50">
    <!-- Header Removed (Using Global AdminNavbar) -->

    <!-- Main Content -->
    <main class="flex-1 overflow-y-auto p-6">

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center h-full">
         <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong class="font-bold">Error!</strong>
        <span class="block sm:inline"> {{ error.message }}</span>
        <button @click="refetch()" class="mt-2 text-sm underline hover:text-red-800">Try Again</button>
      </div>

      <div v-else-if="stats" class="max-w-7xl mx-auto flex flex-col gap-6">
        <div id="tour-home-pulse">
          <PulseRow :stats="stats" />
        </div>
        <div class="flex flex-col lg:flex-row gap-6">

          <div id="tour-home-main-column" class="w-full lg:w-2/3 space-y-6">
            <UpcomingTimetableWidget />
            <MyAssignmentsWidget />
            <PendingScoresWidget />

            <CbtWidget
              v-if="canViewCBT"
              :activeTests="stats.activeCbtTests"
              :upcomingTests="stats.upcomingCbtTests"
            />
          </div>

          <div id="tour-home-side-column" class="w-full lg:w-1/3 space-y-6">
             <AiUsageWidget />
             <MyRolesWidget />
             <OperationsWidget :logs="stats.auditLogs" />
             <RecentAttendanceWidget v-if="canViewAttendance" :attendanceToday="stats.attendanceToday" />
             <AttendanceChart
               v-if="canViewAttendance && stats.attendanceTrend.length > 0"
               :trend="stats.attendanceTrend"
             />
          </div>
        </div>

        <!-- Quick Actions (permission-gated) -->
        <div v-if="visibleQuickActions.length" id="tour-home-quick-actions" class="mt-4 pt-4 pb-10">
          <h3 class="font-bold text-gray-800 text-lg mb-4">Quick Actions</h3>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
             <router-link
               v-for="qa in visibleQuickActions"
               :key="qa.routeName"
               :to="{ name: qa.routeName }"
               class="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition flex flex-col items-center justify-center gap-3 text-center group"
             >
                <div class="w-12 h-12 rounded-full flex items-center justify-center group-hover:scale-110 transition"
                  :class="qa.bgClass">
                  <i :class="['pi', qa.icon, 'text-xl']"></i>
                </div>
                <span class="font-semibold text-gray-700 text-sm">{{ qa.label }}</span>
             </router-link>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/store/userStore';

import { can, AppPermissions } from '@/utils/permissions';
import { routeNames } from '@/constants/route-details.constant';
import { useStaffDashboard } from '@/composables/useStaffDashboard';

// Widgets
import PulseRow from './Home/PulseRow.vue';
import OperationsWidget from './Home/OperationsWidget.vue';
import CbtWidget from './Home/CbtWidget.vue';
import AttendanceChart from './Home/AttendanceChart.vue';
import MyRolesWidget from './Home/MyRolesWidget.vue';
import MyAssignmentsWidget from './Home/MyAssignmentsWidget.vue';
import UpcomingTimetableWidget from './Home/UpcomingTimetableWidget.vue';
import PendingScoresWidget from './Home/PendingScoresWidget.vue';
import RecentAttendanceWidget from './Home/RecentAttendanceWidget.vue';
import AiUsageWidget from './Home/AiUsageWidget.vue';
import { QUERY_DASHBOARD_OVERVIEW } from '@/graphql';

const userStore = useUserStore();

const {
  canCreateStudents,
  canCreateAttendance,
  canCreateBills,
  canUploadScores,
} = useStaffDashboard();

// Permissions
const canViewCBT = computed(() => can(userStore.currentUser, AppPermissions.CBT.Actions.View, AppPermissions.CBT.Resource));
const canViewAttendance = computed(() =>
  can(userStore.currentUser, AppPermissions.Permissions.Actions.View, AppPermissions.Permissions.Resource)
  || can(userStore.currentUser, AppPermissions.Attendance.Actions.View, AppPermissions.Attendance.Resource),
);

const { result, loading, error, refetch } = QUERY_DASHBOARD_OVERVIEW();

const stats = computed(() => result.value?.dashboardOverview);

// Quick Actions — permission-gated
interface QuickAction {
  routeName: string
  label: string
  icon: string
  bgClass: string
  visible: boolean
}

const visibleQuickActions = computed<QuickAction[]>(() => {
  const all: QuickAction[] = [
    {
      routeName: 'admin-student',
      label: 'Register Student',
      icon: 'pi-user-plus',
      bgClass: 'bg-primary/10 text-primary',
      visible: canCreateStudents.value,
    },
    {
      routeName: 'admin-daily-attendance',
      label: 'Mark Attendance',
      icon: 'pi-check-circle',
      bgClass: 'bg-primary/10 text-primary',
      visible: canCreateAttendance.value,
    },
    {
      routeName: routeNames.ADMIN_BILLING_BILLS.name,
      label: 'Create Bill',
      icon: 'pi-file',
      bgClass: 'bg-orange-50 text-orange-600',
      visible: canCreateBills.value,
    },
    {
      routeName: 'admin-results',
      label: 'Upload Result',
      icon: 'pi-upload',
      bgClass: 'bg-purple-50 text-purple-600',
      visible: canUploadScores.value,
    },
  ];
  return all.filter((a) => a.visible);
});
</script>

<style scoped>
/* Scoped styles overrides if necessary */
</style>
